// src/core/catalog.js
// Product catalog, driven by /assets/models/models.config.json.
//
// The JSON config is the single source of truth for per-model placement:
//   - name          display name
//   - noseDistance  forward (+Z) shift in cm to seat on the nose bridge
//   - scale         multiplier on the canonical alignment (1.0 = as-aligned)
//
// Placement is otherwise MediaPipe-native (posed by M·A). noseDistance and
// scale are the only per-model values; everything else is shared.
//
// loadCatalog() fetches and parses the JSON. If it can't be loaded, a built-in
// fallback list is used so the app still runs.

const CONFIG_URL = '/assets/models/models.config.json';

// Lens look. `opacity` is how dark the tint is (0 = clear, 1 = opaque);
// `color` is the tint colour. Reflections come from the environment map.
// Per-model override in models.config.json:  "lens": { "opacity": 0.35, "color": "#2b3a4a" }
const LENS_DEFAULT = {
  enabled: true,
  meshTokens: ['lens', 'glass', 'clear'],
  color: '#2a2a2a',
  opacity: 0.45,
  ior: 1.5,
  roughness: 0.06,
  reflectivity: 0.9,
  envMapIntensity: 1.9
};

// Fallback used only if the JSON config fails to load.
const FALLBACK = {
  defaults: { noseDistance: 0.05, scale: 1.0, pantoTilt: 8, occBias: 0.0, responsiveness: 0.90 },
  models: [
    { sku: 'rayban', name: 'Ray-Ban Classic' },
    { sku: 'GA3198', name: 'GA3198' },
    { sku: 'NV0315F01', name: 'NV0315F01' },
    { sku: 'NV2113A103', name: 'NV2113A103' },
    { sku: 'NV4117F01', name: 'NV4117F01' },
    { sku: 'NV4117F03', name: 'NV4117F03' }
  ]
};

function buildEntry(m, defaults) {
  const pick = (key, fb) => Number.isFinite(m[key]) ? m[key]
    : (Number.isFinite(defaults[key]) ? defaults[key] : fb);
  return {
    sku: m.sku,
    name: m.name || m.sku,
    modelUrl: `/assets/models/${m.sku}.glb`,
    thumbnailUrl: `/assets/thumbnails/${m.sku}.jpg`,
    noseDistance: pick('noseDistance', 0.05),
    scale: pick('scale', 1.0),
    pantoTilt: pick('pantoTilt', 8),
    occBias: pick('occBias', 0.0),            // occluder depth (cm)
    responsiveness: pick('responsiveness', 0.90),  // tracking speed 0..1
    lens: { ...LENS_DEFAULT, ...(m.lens || {}) }
  };
}

// Populated by loadCatalog(); exported binding is updated in place.
export let CATALOG = FALLBACK.models.map(m => buildEntry(m, FALLBACK.defaults));

export async function loadCatalog() {
  try {
    const res = await fetch(CONFIG_URL, { cache: 'no-cache' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const cfg = await res.json();
    const defaults = { ...FALLBACK.defaults, ...(cfg.defaults || {}) };
    const models = Array.isArray(cfg.models) && cfg.models.length ? cfg.models : FALLBACK.models;
    CATALOG = models.map(m => buildEntry(m, defaults));
    console.log(`[catalog] Loaded ${CATALOG.length} models from ${CONFIG_URL}`);
  } catch (err) {
    console.warn(`[catalog] Could not load ${CONFIG_URL} (${err.message}); using fallback list.`);
    CATALOG = FALLBACK.models.map(m => buildEntry(m, FALLBACK.defaults));
  }
  return CATALOG;
}

export function getSku(id) {
  return CATALOG.find(item => item.sku === id) || CATALOG[0];
}
